import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line,
} from "recharts";
import { formatNumber, formatDate } from "@/lib/format";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#0C0C0E] border border-[#16C784]/60 px-3 py-2 font-mono text-xs">
      <div className="text-white mb-1 max-w-[220px] truncate">{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: {formatNumber(p.value)}
        </div>
      ))}
    </div>
  );
};

const PerformanceChart = ({ videos, snapshots }) => {
  const [tab, setTab] = useState("views");

  const viewsData = useMemo(() => {
    return [...videos]
      .filter((v) => v.published_at)
      .sort((a, b) => new Date(a.published_at) - new Date(b.published_at))
      .slice(-30)
      .map((v) => ({
        name: v.title,
        date: formatDate(v.published_at),
        views: v.view_count,
        likes: v.like_count,
      }));
  }, [videos]);

  const growthData = useMemo(() => {
    return (snapshots || []).map((s) => ({
      date: formatDate(s.timestamp),
      subscribers: s.subscriber_count,
      views: s.view_count,
    }));
  }, [snapshots]);

  const hasGrowth = growthData.length > 1;

  return (
    <motion.div
      data-testid="performance-chart"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-[#0C0C0E] border border-[#222226] p-5 md:p-6"
      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)" }}
    >
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635]">// Telemetry</div>
          <h2 className="font-rajdhani font-bold text-xl tracking-wide mt-1">
            {tab === "views" ? "VIEWS PER UPLOAD" : "CHANNEL GROWTH"}
          </h2>
        </div>
        <div className="flex border border-[#222226]">
          <button
            data-testid="chart-tab-views"
            onClick={() => setTab("views")}
            className={`font-rajdhani uppercase tracking-wider text-xs px-4 py-2 transition-colors duration-200 ${
              tab === "views" ? "bg-[#16C784] text-white" : "text-[#A0A0AB] hover:text-white"
            }`}
          >
            Views
          </button>
          <button
            data-testid="chart-tab-growth"
            onClick={() => setTab("growth")}
            className={`font-rajdhani uppercase tracking-wider text-xs px-4 py-2 transition-colors duration-200 ${
              tab === "growth" ? "bg-[#16C784] text-white" : "text-[#A0A0AB] hover:text-white"
            }`}
          >
            Growth
          </button>
        </div>
      </div>

      <div className="h-[300px]">
        {tab === "views" ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={viewsData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#16C784" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#16C784" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1e" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#5a5a63", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#222226" }} minTickGap={30} />
              <YAxis tickFormatter={formatNumber} tick={{ fill: "#5a5a63", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} width={45} />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#16C784", strokeOpacity: 0.3 }} />
              <Area type="monotone" dataKey="views" name="views" stroke="#16C784" strokeWidth={2} fill="url(#gViews)" dot={false} activeDot={{ r: 4, fill: "#16C784" }} />
            </AreaChart>
          </ResponsiveContainer>
        ) : hasGrowth ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={growthData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1e" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#5a5a63", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#222226" }} />
              <YAxis tickFormatter={formatNumber} tick={{ fill: "#5a5a63", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} width={45} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="subscribers" name="subscribers" stroke="#A3E635" strokeWidth={2} dot={{ r: 3, fill: "#A3E635" }} />
              <Line type="monotone" dataKey="views" name="views" stroke="#16C784" strokeWidth={2} dot={{ r: 3, fill: "#16C784" }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center gap-2">
            <div className="font-rajdhani font-bold text-lg text-[#A0A0AB] tracking-wide">GROWTH TRACKING ARMED</div>
            <p className="font-mono text-xs text-[#5a5a63] max-w-sm">
              Growth history builds each time you sync. Hit SYNC over the coming days to plot your subscriber &amp; view trajectory.
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default PerformanceChart;
